import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useEffect, useRef} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {HeadingXSmall, LabelLarge} from "@khanacademy/wonder-blocks-typography";
import {isActivitiesPage} from "@khan/guide-core-helpers";
import {useGuideState} from "@khan/guide-state";
import {mediaQueries} from "@khan/shared-styles";
import {useLocation} from "@khan/urls";

import {AsStudentToggle} from "../as-student-toggle.tsx";

import {HEADER_HEIGHT} from "./constants.ts";
import {HeaderMenu} from "./header-menu.tsx";
import {PowerIndicator} from "./power-indicator.tsx";
import type {HeaderType} from "./types.ts";
import {usePropsForHeaderType} from "./use-props-for-header-type.ts";

import collapseIcon from "@phosphor-icons/core/regular/arrows-in-simple.svg";
import expandIcon from "@phosphor-icons/core/regular/arrows-out-simple.svg";
import activitiesListIcon from "@phosphor-icons/core/regular/list-dashes.svg";
import xIcon from "@phosphor-icons/core/regular/x.svg";

interface ChatMenuProps {
    /* Is the menu open? */
    menuOpen: boolean;
    /* Callback for when the menu icon is clicked. */
    onToggleMenu: () => unknown;
    /* Callback for when the preference menu option is clicked. */
    onSelectPreferences: () => unknown;
    /* Callback for when the refresh chat menu option is clicked. */
    onResetChat: () => unknown;
}

/**
 * These chat menu props are necessary for states that utilize dev
 * menu options. Currently, the desktop chat and small screen chat are
 * the only guide states that use dev menu items.
 */
interface ChatMenuDevOnlyProps {
    onOpenThreadHistory: () => unknown;
    /* Request the current thread be downloaded. */
    onDownloadThread: () => unknown;
    /* Show additional dev-related menu items */
    showDevControls: boolean;
    /* Enable the thread history menu item (if shown) */
    threadHistoryEnabled: boolean;
}

type SharedChatProps = ChatMenuProps &
    ChatMenuDevOnlyProps & {
        /* Callback for when the close icon is clicked. */
        onClose: () => unknown;
    };

/* The desktop version shown on non-activities pages */
type DesktopChatProps = {
    type: HeaderType.DesktopChat;
} & SharedChatProps & {
        /* Is the chat window expanded? */
        expanded: boolean;
        /* Callback for when the expand icon is clicked. */
        onToggleExpanded: () => unknown;
        /* Should we disable the expand/collapse icon? */
        expandDisabled: boolean;
    };

/* The small screen version shown on non-activities pages */
type SmallScreenChatProps = {
    type: HeaderType.SmallScreenChat;
} & SharedChatProps;

type SharedActivityProps = ChatMenuProps & {
    /* The activity title displayed in the header. */
    activityTitle: string;
    activityAssignable: boolean;
    activityContentDescriptor: string | null | undefined;
};

/* The desktop version shown on activities pages */
type DesktopActivityProps = {
    type: HeaderType.DesktopActivity;
} & SharedActivityProps &
    AssignedActivityProps;

/* The small screen version shown on activities pages */
type SmallScreenActivityProps = {
    type: HeaderType.SmallScreenActivity;
} & SharedActivityProps &
    AssignedActivityProps & {
        /* Callback for when the activities list icon is clicked. */
        onClickActivities: () => unknown;
    };

interface AssignedActivityProps {
    assignedActivityClassroomName: string | null | undefined;
}

type AIToolsProps = {
    type: HeaderType.AITools;
    closable: boolean;
    /* The activity title displayed in the header. */
    /* We only set this for "guide only" tools. */
    activityTitle?: string;
} & SharedChatProps;

type ChromeExtensionProps = {
    type: HeaderType.ChromeExtension;
} & ChatMenuProps;

type Props =
    | ChromeExtensionProps
    | DesktopActivityProps
    | DesktopChatProps
    | SmallScreenActivityProps
    | SmallScreenChatProps
    | AIToolsProps;

/**
 * A shared header for Khanmigo that is used in multiple versions of the UI.
 *
 * @param props
 * @returns {JSX.Element}
 */
export const Header = (props: Props): React.ReactElement => {
    const isHighTraffic = useGuideState("isHighTraffic");
    const remainingQuota = useGuideState("remainingQuota");
    const threadId = useGuideState("threadId");
    const {pathname} = useLocation();
    const {isChatHeader, showKhanmigoLabel, showStudentToggle} =
        usePropsForHeaderType(props.type);

    // HACK(casting to any was the only way I could get TS to pass this check)
    const chatHeaderStyle: any = [
        styles.header,
        isChatHeader && styles.chatHeader,
        props.type === "small-screen-chat" && styles.smallScreenChatHeader,
        props.type === "desktop-activity" && styles.desktopActivityHeader,
        props.type === "small-screen-activity" &&
            styles.smallScreenActivityHeader,
    ];

    const closeButton = useRef<HTMLButtonElement>(null);
    const open = useGuideState("open");
    useEffect(() => {
        if (open && closeButton.current) {
            closeButton.current?.focus();
        }
    }, [open]);

    return (
        <View
            style={chatHeaderStyle}
            testId="chat-bot-header"
            id="khanmigo-header"
        >
            {props.type === "small-screen-activity" && (
                <View style={styles.subheader}>
                    <IconButton
                        icon={activitiesListIcon}
                        onClick={props.onClickActivities}
                        aria-label={t`Activities List`}
                        kind="tertiary"
                        actionType="neutral"
                        size="small"
                        testId="open-activities"
                    />
                    <Strut size={spacing.medium_16} />
                </View>
            )}
            {showKhanmigoLabel && (
                <View style={styles.subheader}>
                    <View
                        style={[
                            styles.khanmigoLabel,
                            // On activities pages, in portrait mode, tablets don't have enough
                            // screen space to show both the label and longer activity titles.
                            isActivitiesPage(pathname) &&
                                styles.hideKhanmigoLabelForSmallScreens,
                        ]}
                    >
                        <LabelLarge>{t`Khanmigo`}</LabelLarge>
                        {/*TODO (GL-838) Replace with WB Pill component when available.*/}
                        <View style={styles.betaLabel}>{t`BETA`}</View>
                    </View>
                    {showStudentToggle && (
                        <AsStudentToggle threadId={threadId} light={true} />
                    )}
                </View>
            )}
            {(props.type === "desktop-activity" ||
                props.type === "small-screen-activity" ||
                (props.type === "ai-tools" && props.activityTitle)) && (
                <View>
                    <LabelLarge tag="h2" style={styles.activityTitle}>
                        {props.activityTitle}
                    </LabelLarge>
                    {(props.type === "desktop-activity" ||
                        props.type === "small-screen-activity") &&
                        props.assignedActivityClassroomName && (
                            <HeadingXSmall
                                style={styles.assignmentClassroomName}
                            >
                                {props.assignedActivityClassroomName}
                            </HeadingXSmall>
                        )}
                </View>
            )}
            <View style={styles.subheader}>
                {/*If we're showing the teacher toggle on small screen activities,*/}
                {/*we display it to the right of the activity title.*/}
                {showStudentToggle &&
                    props.type === "small-screen-activity" && (
                        <AsStudentToggle threadId={threadId} light={true} />
                    )}
                <PowerIndicator
                    isHighTraffic={isHighTraffic}
                    remainingQuota={remainingQuota}
                    style={[
                        styles.subheader,
                        styles.padButtonLeft,
                        styles.padButtonRight,
                    ]}
                    color={color.offBlack64}
                />
                <HeaderMenu {...props} />
                {/* Desktop chat has expand button */}
                {props.type === "desktop-chat" && !props.expandDisabled ? (
                    <IconButton
                        icon={props.expanded ? collapseIcon : expandIcon}
                        size="small"
                        kind="tertiary"
                        actionType="neutral"
                        disabled={props.expandDisabled}
                        style={styles.padButton}
                        onClick={props.onToggleExpanded}
                        aria-label={
                            props.expanded
                                ? t`Make chat window smaller`
                                : t`Make chat window larger`
                        }
                    />
                ) : (
                    // To preserve the header layout, and the relative position
                    // of the menu toggle, we render a strut instead
                    // of the button when the expand button is disabled.
                    <Strut size={36} />
                )}

                {/* Non-activities versions have a close button */}
                {(props.type === "desktop-chat" ||
                    props.type === "small-screen-chat" ||
                    (props.type === "ai-tools" && props.closable)) && (
                    <>
                        <IconButton
                            id="khanmigo-chat-ui-toggle"
                            aria-label={t`Close Khanmigo chat window`}
                            onClick={props.onClose}
                            testId="close-chat-window"
                            style={[styles.padButton, styles.noPadButtonRight]}
                            icon={xIcon}
                            size="small"
                            kind="tertiary"
                            actionType="neutral"
                            ref={closeButton}
                        />
                    </>
                )}
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    activityTitle: {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        alignSelf: "center",
    },
    assignmentClassroomName: {
        color: color.offBlack64,
        alignSelf: "center",
        maxWidth: 500, // width or maxWidth required to make ellipsis work
        [mediaQueries.smOrSmaller]: {
            maxWidth: 250, // width or maxWidth required to make ellipsis work
        },
        // Styles below required to make ellipsis work
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    betaLabel: {
        fontSize: 8,
        fontWeight: "bold",
        color: color.offBlack64,
        marginBlockEnd: spacing.xSmall_8,
    },
    chatHeader: {
        borderStartStartRadius: 8,
        borderStartEndRadius: 8,
    },
    smallScreenActivityHeader: {
        alignContent: "space-evenly",
    },
    smallScreenChatHeader: {
        borderRadius: 0,
        color: color.offBlack,
        backgroundColor: color.offWhite,
    },
    header: {
        flexDirection: "row",
        flexWrap: "nowrap",
        flexShrink: 0,
        height: HEADER_HEIGHT,
        width: "100%",
        justifyContent: "space-between",
        alignItems: "center",
        color: color.offBlack,
        backgroundColor: color.offWhite,
        padding: "9px 8px",
        paddingInlineStart: spacing.medium_16,
        paddingInlineEnd: 16,
        borderRadius: 0,
    },
    hideKhanmigoLabelForSmallScreens: {
        [mediaQueries.mdOrSmaller]: {
            display: "none",
        },
    },
    khanmigoLabel: {
        flexDirection: "row",
    },
    desktopActivityHeader: {
        [mediaQueries.mdOrLarger]: {
            padding: "9px 32px",
        },
    },
    subheader: {
        flexDirection: "row",
        alignItems: "center",
    },
    padButton: {
        // Takes up the outline space of the button, but doesn't add any
        // padding.
        margin: `0 ${spacing.xxxxSmall_2}px`,
    },
    noPadButtonRight: {
        marginInlineEnd: 0,
    },
    padButtonLeft: {
        marginInlineStart: spacing.xSmall_8,
    },
    padButtonRight: {
        marginInlineEnd: spacing.xSmall_8,
    },
});
